import { useEffect, useRef, useState } from 'react';
import { ArrowLeft, ArrowRight, CheckCircle2, Lock } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useGame } from '../../context/GameContext';
import { matchesLevelKey } from '../../utils/ids';
import type { CatalogWorld } from '../../utils/worldCategories';
import { WorldIcon } from './WorldIcon';

const navigationClass = 'inline-flex h-11 w-11 items-center justify-center rounded-lg border border-gray-300 bg-white text-gray-700 transition-colors hover:border-primary-500 hover:text-primary-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-500 disabled:cursor-default disabled:opacity-35 disabled:hover:border-gray-300 disabled:hover:text-gray-700 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 dark:hover:text-primary-300';

export function HomeWorldCarousel({ worlds }: { worlds: CatalogWorld[] }) {
  const { gameData, isWorldUnlocked } = useGame();
  const [pageSize, setPageSize] = useState(3);
  const [page, setPage] = useState(0);
  const trackRef = useRef<HTMLDivElement>(null);
  const pageCount = Math.ceil(worlds.length / pageSize);
  const pages = Array.from({ length: pageCount }, (_, index) => worlds.slice(index * pageSize, (index + 1) * pageSize));

  useEffect(() => {
    const desktop = window.matchMedia('(min-width: 1024px)');
    const tablet = window.matchMedia('(min-width: 640px)');
    const update = () => setPageSize(desktop.matches ? 3 : tablet.matches ? 2 : 1);
    update();
    desktop.addEventListener('change', update);
    tablet.addEventListener('change', update);
    return () => {
      desktop.removeEventListener('change', update);
      tablet.removeEventListener('change', update);
    };
  }, []);

  useEffect(() => {
    setPage(0);
    trackRef.current?.scrollTo({ left: 0, behavior: 'instant' });
  }, [pageSize, worlds]);

  function goToPage(nextPage: number) {
    const track = trackRef.current;
    const target = track?.children[Math.max(0, Math.min(nextPage, pageCount - 1))] as HTMLElement | undefined;
    if (!track || !target) return;
    track.scrollTo({
      left: target.offsetLeft,
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth',
    });
  }

  if (!worlds.length) return null;

  return <div className="mt-8">
    <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
      <p aria-live="polite" aria-atomic="true" className="text-sm tabular-nums text-gray-500 dark:text-gray-400">
        Worlds {page * pageSize + 1}–{Math.min((page + 1) * pageSize, worlds.length)} of {worlds.length}
      </p>
      {pageCount > 1 && <div className="flex items-center gap-2" aria-label="Browse worlds">
        <button type="button" onClick={() => goToPage(page - 1)} disabled={page === 0} aria-label="Previous worlds" aria-controls="home-worlds-track" className={navigationClass}><ArrowLeft size={19} aria-hidden="true" /></button>
        <button type="button" onClick={() => goToPage(page + 1)} disabled={page === pageCount - 1} aria-label="Next worlds" aria-controls="home-worlds-track" className={navigationClass}><ArrowRight size={19} aria-hidden="true" /></button>
      </div>}
    </div>
    <div
      id="home-worlds-track"
      ref={trackRef}
      role="region"
      aria-label="Available worlds"
      aria-roledescription="carousel"
      tabIndex={0}
      onScroll={() => {
        const track = trackRef.current;
        if (!track) return;
        const slides = Array.from(track.children) as HTMLElement[];
        const nearest = slides.reduce((best, slide, index) => Math.abs(slide.offsetLeft - track.scrollLeft) < Math.abs(slides[best].offsetLeft - track.scrollLeft) ? index : best, 0);
        setPage(nearest);
      }}
      onKeyDown={event => {
        if (event.target !== event.currentTarget) return;
        if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
          event.preventDefault();
          goToPage(page + (event.key === 'ArrowRight' ? 1 : -1));
        }
      }}
      className="relative flex snap-x snap-mandatory gap-4 overflow-x-auto overscroll-x-contain rounded-xl pb-3 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-primary-500"
    >
      {pages.map((members, pageIndex) => <div key={pageIndex} role="group" aria-roledescription="slide" aria-label={`${pageIndex + 1} of ${pageCount}`} className="grid w-full shrink-0 snap-start snap-always auto-rows-fr grid-cols-1 content-start gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {members.map(({ info, world }, index) => {
          const completedCount = world.levels.filter(level => gameData?.progress.completedLevels.some(id => matchesLevelKey(id, world.id, level.id))).length;
          const isUnlocked = info.alwaysUnlocked || isWorldUnlocked(world.id, world);
          const isComplete = world.levels.length > 0 && completedCount === world.levels.length;
          const card = <>
            <div className="mb-5 flex items-center justify-between text-primary-600 dark:text-primary-400"><WorldIcon name={world.icon} size={23} /><span className="font-mono text-xs text-gray-400 dark:text-gray-500">{String(pageIndex * pageSize + index + 1).padStart(2, '0')}</span></div>
            <h3 className="text-base font-semibold leading-snug text-gray-900 dark:text-gray-100">{world.name}</h3>
            <p className="mt-3 flex-1 text-sm leading-relaxed text-gray-600 dark:text-gray-300">{world.description}</p>
            <div className="mt-6 flex items-center justify-between gap-3 border-t border-gray-100 pt-4 text-xs dark:border-gray-700">
              <span className="text-gray-500 dark:text-gray-400">{completedCount > 0 ? `${completedCount} / ${world.levels.length} completed` : `${world.levels.length} levels`}</span>
              <span className={`inline-flex shrink-0 items-center gap-1.5 font-semibold ${isUnlocked ? 'text-primary-700 dark:text-primary-300' : 'text-gray-500 dark:text-gray-400'}`}>{isUnlocked ? <>{isComplete ? 'Revisit world' : 'Open world'} {isComplete ? <CheckCircle2 size={15} aria-hidden="true" /> : <ArrowRight size={15} aria-hidden="true" />}</> : <>Locked <Lock size={14} aria-hidden="true" /></>}</span>
            </div>
          </>;
          const cardClass = 'flex h-full min-w-0 flex-col rounded-xl border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-gray-800';
          return isUnlocked ? <Link key={world.id} to={`/worlds/${world.id}`} className={`${cardClass} transition-colors hover:border-primary-500 hover:bg-primary-50/30 focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-primary-500 dark:hover:border-primary-400 dark:hover:bg-gray-800/70`}>{card}</Link> : <div key={world.id} className={`${cardClass} opacity-60`}>{card}</div>;
        })}
      </div>)}
    </div>
  </div>;
}
